/**
 * Was von einem Auftrag übrig bleibt.
 * ===========================================================================
 *
 * ORIGINALTEXT MARVIN (06.09.2026)
 *   „wo trage ich nun die preise zu den positionen ein? die sehe ja nur ich.
 *    niemand anderes und auch der vergleich und was dann faktisch pro auftrag
 *    bei mir bleibt und das kann ich dann alles auswerten."
 *
 * DIE KARTE „GELD" DARÜBER BEANTWORTET DIESELBE FRAGE ALS SUMME. Das ist die
 * Zahl für den Monat; diese Liste ist die Zahl für die BAUSTELLE. Beide kommen
 * aus denselben zwei Sichten, und Migration 160 misst in ihrer Gegenprobe,
 * dass sie sich zur selben Summe addieren - **zwei Zahlen über dieselbe Frage,
 * die auseinanderlaufen, sind schlimmer als eine.**
 *
 * ---------------------------------------------------------------------------
 * DER REST IST EINE BESTANDSZAHL UND KEIN ZEITRAUM
 * ---------------------------------------------------------------------------
 *
 * Das steht als Satz über der Tabelle, und es ist kein Kleingedrucktes. Eine
 * Gutschrift trägt ihr Gutschriftdatum, eine Rechnung des Nachunternehmers ihr
 * Rechnungsdatum, und dazwischen liegen Wochen. Ein Zeitraumfilter auf den
 * REST zeigte im einen Monat den vollen Zufluss ohne Kosten und im anderen
 * einen Rest tief im Minus - **beides erfunden, und beides sieht aus wie eine
 * Messung.**
 *
 * Der Zeitraum oben auf dem Bildschirm entscheidet deshalb, WELCHE Aufträge
 * hier stehen, und nicht, mit welchen Beträgen. Genau diese Sorte Verwechslung
 * ist der Grund, warum in dieser Auswertung seit dem 30.08.2026 jede Karte
 * ihre Sorte im Kopf trägt.
 *
 * ---------------------------------------------------------------------------
 * UND EIN AUFTRAG OHNE GUTSCHRIFT IST NICHT EINER MIT REST NULL
 * ---------------------------------------------------------------------------
 *
 * Dort steht „noch offen" und keine 0. **Eine geratene Null sieht aus wie eine
 * gemessene** - und die Null hier läse sich als „an dieser Baustelle ist
 * nichts übriggeblieben", während sie „die Telekom hat noch nicht bezahlt"
 * heisst. Das sind zwei verschiedene Sachen mit zwei verschiedenen nächsten
 * Schritten.
 */

import { useEffect, useState } from 'react';
import { api, baustelle, datum, menge } from '../api.ts';
import { Fehlerkasten, ausFehler, type Fehlerlage } from './Formular.tsx';

/** Euro, deutsch, mit 2 Nachkommastellen. */
function euro(w: unknown): string {
  const n = Number(w ?? 0);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('de-DE',
    { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

const SORTEN: { wert: string; text: string }[] = [
  { wert: 'rest',    text: 'Rest, kleinster zuerst' },
  { wert: 'rest_ab', text: 'Rest, grösster zuerst' },
  { wert: 'zufluss', text: 'Zufluss' },
  { wert: 'abfluss', text: 'Abfluss' },
  { wert: 'neuste',  text: 'letzte Bewegung' },
  { wert: 'adresse', text: 'Anschrift' },
];

const FILTER: { wert: string; text: string; dazu: string }[] = [
  { wert: '',         text: 'Alle',
    dazu: 'Jeder Auftrag, an dem Geld bewegt wurde.' },
  { wert: 'minus',    text: 'Rest im Minus',
    dazu: 'Der Nachunternehmer hat mehr gekostet, als gutgeschrieben wurde.' },
  { wert: 'offen',    text: 'noch keine Gutschrift',
    dazu: 'Abgerechnet, aber die Telekom hat noch nicht bezahlt.' },
  { wert: 'unlesbar', text: 'Betrag fehlt',
    dazu: 'Eine Gutschrift kam an und ihr PDF war nicht zu lesen. '
        + 'Sie fehlt in jeder Summe.' },
];

export function GeldJeAuftrag({ von, bis }: { von: string; bis: string }) {
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [sortierung, setSortierung] = useState('rest');
  const [nur, setNur] = useState('');
  const [offen, setOffen] = useState(false);

  useEffect(() => {
    if (!offen) return;
    setFehler(null);
    api.auswertungGeldAuftraege({ von, bis, sortierung, nur })
      .then(setD)
      .catch((f) => { setD(null); setFehler(ausFehler(f)); });
  }, [offen, von, bis, sortierung, nur]);

  /* SIE STEHT ZUGEKLAPPT DA. Die Karte darüber beantwortet die Frage „wie
     steht der Monat"; diese Liste beantwortet „welche Baustelle kostet mich
     Geld", und das fragt man nicht bei jedem Öffnen. Der Knopf sagt, was
     darin steht - **eine Klappe ohne Aufschrift macht man nicht auf.** */
  if (!offen) {
    return (
      <button className="knopf" style={{ marginTop: 12 }}
              onClick={() => setOffen(true)}>
        Je Auftrag aufschlüsseln: was bleibt an welcher Baustelle
      </button>
    );
  }

  const k = d?.kopf;

  return (
    <div style={{ marginTop: 12 }}>
      <Fehlerkasten lage={fehler} />

      {/* DER SATZ STEHT ÜBER DER TABELLE UND NICHT ALS FUSSNOTE. Wer die
          Zahlen liest, liest ihn mit - dieselbe Bauart wie bei „Der Rest ist
          nicht der Gewinn" eine Karte weiter oben. */}
      <div className="hinweis">
        <b>Diese Beträge sind Bestandszahlen und kein Zeitraum.</b> Sie stehen
        je Auftrag über seine ganze Laufzeit. Eine Gutschrift trägt ihr
        Gutschriftdatum, eine Rechnung des Nachunternehmers ihr
        Rechnungsdatum - dazwischen liegen oft Wochen. Der eingestellte
        Zeitraum entscheidet deshalb, <b>welche</b> Aufträge hier stehen (die
        mit einer Bewegung darin), und nicht, mit welchen Beträgen.
      </div>

      <div className="reihe reiterreihe" style={{ marginTop: 10 }}>
        {FILTER.map((f) => (
          <button key={f.wert} className="knopf" aria-current={nur === f.wert}
                  onClick={() => setNur(f.wert)} title={f.dazu}>
            {f.text}
            {f.wert === 'minus' && k ? ` ${menge(k.im_minus)}` : ''}
            {f.wert === 'offen' && k ? ` ${menge(k.ohne_gutschrift)}` : ''}
            {f.wert === 'unlesbar' && k ? ` ${menge(k.belege_unlesbar)}` : ''}
          </button>
        ))}
        <div className="wachsen" />
        <label className="feldpaar klein leise">
          Sortiert nach
          <select className="klein" value={sortierung}
                  onChange={(e) => setSortierung(e.target.value)}>
            {SORTEN.map((s) => (
              <option key={s.wert} value={s.wert}>{s.text}</option>
            ))}
          </select>
        </label>
        <button className="knopf" onClick={() => setOffen(false)}>
          zuklappen
        </button>
      </div>

      {/* DER SATZ ZUM GEWÄHLTEN FILTER. Ein Chip mit einem Wort darauf sagt
          nicht, was er weglässt. */}
      <p className="klein leise" style={{ margin: '8px 0 0' }}>
        {FILTER.find((f) => f.wert === nur)?.dazu}
      </p>

      {!d ? (
        <p className="klein leise" style={{ marginTop: 8 }}>wird geladen …</p>
      ) : d.zeilen.length === 0 ? (
        /* EINE LEERE LISTE SAGT, WAS SIE LEER MACHT. Sonst liest sie sich wie
           „es gibt keine Aufträge". */
        <p className="klein leise" style={{ marginTop: 8 }}>
          In diesem Zeitraum wurde an keinem Auftrag Geld bewegt
          {nur ? ', der zu diesem Filter passt' : ''}.
        </p>
      ) : (
        <>
          <div className="kachelreihe" style={{ marginTop: 10 }}>
            <div className="kachel">
              <div className="wert">{menge(k.auftraege)}</div>
              <div className="was">Aufträge in dieser Liste</div>
            </div>
            <div className="kachel">
              <div className="wert" style={{ color: 'var(--erledigt)' }}>
                {euro(k.zufluss)}
              </div>
              <div className="was">Zufluss zusammen</div>
            </div>
            <div className="kachel">
              <div className="wert" style={{ color: 'var(--ueberfaellig)' }}>
                {euro(k.abfluss)}
              </div>
              <div className="was">Abfluss zusammen</div>
            </div>
            {/* DIE AUFSCHRIFT SAGT, WELCHE AUFTRÄGE DARIN STECKEN.
                „Rest zusammen" stand hier und war die teuerste Zeile dieser
                Karte: sie ist NICHT Zufluss minus Abfluss - die Aufträge ohne
                Gutschrift bringen ihren Abfluss mit und ihren Rest nicht.
                Wer die beiden Zahlen daneben voneinander abzieht, bekommt
                etwas anderes heraus, und dann glaubt er keiner von beiden.
                **Eine Aufschrift, die mehr behauptet als die Zahl hergibt,
                ist schlimmer als eine fehlende Zahl.** */}
            <div className="kachel">
              <div className="wert">{euro(k.rest)}</div>
              <div className="was">Rest der bezahlten Aufträge</div>
            </div>
          </div>

          {/* WAS DIE SUMME UNVOLLSTÄNDIG MACHT, STEHT DARÜBER. */}
          {k.ohne_gutschrift > 0 && (
            <p className="klein leise" style={{ margin: '8px 0 0' }}>
              {menge(k.ohne_gutschrift)} dieser Aufträge tragen noch keine
              Gutschrift. Sie stehen in der Liste, ihr Rest ist leer, und sie
              gehen in die letzte Kachel <b>nicht</b> ein - ein Auftrag ohne
              Gutschrift ist nicht einer mit Rest 0.
              {' '}<b>Deshalb ist „Rest der bezahlten Aufträge" nicht Zufluss
              minus Abfluss:</b> ihr Abfluss von {euro(k.abfluss_unbezahlt)}
              {' '}ist in der Kachel „Abfluss zusammen" enthalten und in der
              letzten nicht. Die Karte oben rechnet über den ganzen Zeitraum
              und zieht beides voneinander ab.
            </p>
          )}
          {k.belege_unlesbar > 0 && (
            <p className="klein leise" style={{ margin: '4px 0 0' }}>
              Bei {menge(k.belege_unlesbar)} Gutschrift(en) war der Betrag
              nicht zu lesen. Sie fehlen in jeder Zahl darüber; nachtragen
              lassen sie sich in der Liste unter dieser Karte.
            </p>
          )}

          <div className="rollt" style={{ marginTop: 10 }}>
            <table className="liste">
              <thead>
                <tr>
                  <th>Baustelle</th>
                  <th style={{ width: 130 }}>Rahmenvertrag</th>
                  <th style={{ width: 120 }}>Belegnummer</th>
                  <th className="zahl" style={{ width: 120 }}>Zufluss</th>
                  <th className="zahl" style={{ width: 120 }}>Abfluss</th>
                  <th className="zahl" style={{ width: 120 }}>Rest</th>
                  <th style={{ width: 110 }}>letzte Bewegung</th>
                </tr>
              </thead>
              <tbody>
                {d.zeilen.map((z: any) => (
                  <tr key={z.auftrag_id} style={{ cursor: 'default' }}>
                    <td>
                      {baustelle(z)}
                      {z.archiviert && (
                        <span className="merker" style={{ marginLeft: 6 }}>
                          archiviert
                        </span>
                      )}
                      <div className="klein leise">
                        {menge(z.gutschriften)} Gutschrift(en),
                        {' '}{menge(z.rechnungen)} Rechnung(en),
                        {' '}{menge(z.leb_bezahlt)} von {menge(z.leb_dateien)}
                        {' '}LEB bezahlt
                      </div>
                    </td>
                    <td className="klein">{z.rahmenvertrag ?? '-'}</td>
                    <td className="klein">{z.belegnummer ?? '-'}</td>
                    <td className="zahl">
                      {z.zufluss != null ? euro(z.zufluss) : '-'}
                      {z.zufluss_offen > 0 && (
                        <div className="klein rot">
                          {menge(z.zufluss_offen)} ohne Betrag
                        </div>
                      )}
                    </td>
                    <td className="zahl">
                      {z.abfluss != null ? euro(z.abfluss) : '-'}
                    </td>
                    {/* DER REST IST LEER, SOLANGE KEINE GUTSCHRIFT DA IST -
                        und dort steht, was wirklich der Fall ist. */}
                    <td className="zahl einfarbig">
                      {z.rest == null ? (
                        <span className="klein leise">noch offen</span>
                      ) : (
                        <b style={{ color: Number(z.rest) < 0
                                    ? 'var(--ueberfaellig)' : undefined }}>
                          {euro(z.rest)}
                        </b>
                      )}
                    </td>
                    <td className="klein leise">
                      {datum(z.letzte_bewegung)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ZUM MITNEHMEN. Wer mit diesen Zahlen in eine Verhandlung geht,
              steht dabei nicht vor diesem Bildschirm. Semikolon und BOM,
              damit Excel es auf Deutsch öffnet - wie beim Report der
              Übernahme. */}
          <div className="reihe" style={{ marginTop: 10 }}>
            <a className="knopf" download={`geld-je-auftrag_${von}_${bis}.csv`}
               href={csvAdresse(d.zeilen)}>
              Als CSV herunterladen
            </a>
            {d.gekappt && (
              <span className="klein leise">
                Es werden 500 Aufträge gezeigt; die Kacheln darüber zählen
                alle. Der Zeitraum oder ein Filter grenzt weiter ein.
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Die Liste als CSV.
 *
 * SIE ENTSTEHT AUS DEM, WAS AUF DEM BILDSCHIRM STEHT, und nicht aus einer
 * zweiten Abfrage: zwei Wege über dieselbe Frage laufen beim ersten Nachtrag
 * auseinander, und dann trägt die Datei andere Zahlen als die Tabelle daneben.
 *
 * EIN LEERER REST BLEIBT LEER, auch hier. Eine 0 in einer Tabellenkalkulation
 * wird addiert, und danach ist die Summe zu klein - genau der Fehler, den die
 * Spalte auf dem Bildschirm vermeidet.
 */
function csvAdresse(zeilen: any[]): string {
  const kopf = [
    'Strasse', 'Hausnummer', 'PLZ', 'Ort', 'Region', 'KLS-ID', 'SM-Nummer',
    'Rahmenvertrag', 'Belegnummer', 'Gutschriften', 'Zufluss', 'Rechnungen',
    'Abfluss', 'Rest', 'LEB-Dateien', 'davon bezahlt', 'letzte Bewegung',
  ];
  const zahl = (w: unknown) =>
    w == null ? '' : String(w).replace('.', ',');
  const feld = (w: unknown) => {
    const s = w == null ? '' : String(w);
    return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const zeilenText = zeilen.map((z) => [
    z.strasse, `${z.hausnummer ?? ''}${z.hausnummer_zusatz ?? ''}`,
    z.plz, z.ort_text, z.region, z.kls_id, z.sm_nummer,
    z.rahmenvertrag, z.belegnummer,
    z.gutschriften, zahl(z.zufluss), z.rechnungen, zahl(z.abfluss),
    zahl(z.rest), z.leb_dateien, z.leb_bezahlt, z.letzte_bewegung,
  ].map(feld).join(';'));
  const text = '﻿' + [kopf.join(';'), ...zeilenText].join('\r\n');
  return 'data:text/csv;charset=utf-8,' + encodeURIComponent(text);
}
